import { getElementDimensions } from './utils-get-element-dimensions.js';
import { d as debounce } from './debounce-Dg7s1ivP.js';

class HeaderElement extends HTMLElement {
  connectedCallback() {
    this.lastScrollPosition = window.scrollY;
    this.getHeaderHeight();
    this.handleScroll();
    this.updateHeaderTop(0, window.HEADER_HEIGHT);

    window.addEventListener('resize', debounce(this.getHeaderHeight, 100));
    window.addEventListener('scroll', this.handleScroll);

    this.primaryNavs = this.querySelectorAll('[data-nav-primary]');

    // Add event listener to handle clicks on primary nav
    this.primaryNavs.forEach((nav) => {
      nav.addEventListener('click', (event) => {
        const controller = event.target.closest('[data-dropdown-controller]');
        if (!controller) return;

        event.preventDefault();
        this.handleNavDrawerEvent(event);
      });
    });

    // Open the dropdown when hovering over a dropdown-controller in primary nav
    this.primaryNavs.forEach((nav) => {
      nav.addEventListener('mouseover', (event) => {
        if (document.documentElement.classList.contains('touch')) return;
        if (!event.target.closest('[data-dropdown-controller]')) return;
        this.handleNavDrawerEvent(event);
      });

      nav.addEventListener('mouseleave', () => {
        if (document.documentElement.classList.contains('touch')) return;
        const dropdown = this.querySelector('[aria-hidden="false"]');
        if (!dropdown) return;

        // Add timeout to allow moving mouse to dropdown
        setTimeout(() => {
          if (!dropdown.matches(':hover')) {
            dropdown.close();
          }
        }, 100);
      });
    });

    // Handle hovering over dropdowns
    this.addEventListener('mouseover', (event) => {
      if (document.documentElement.classList.contains('touch')) return;
      const dropdown = event.target.closest('dropdown-element');
      if (!dropdown) {
        const clickableElement = event.target.closest('a, button, [role="button"]');
        if (!clickableElement || clickableElement.closest('dropdown-element')) return;
        this.handleNavLinkEvent(event);
      }
    });

    // On page scroll, close any open dropdown
    window.addEventListener('scroll', () => {
      this.querySelector('[aria-hidden="false"]')?.close();
    });

    // Debounced - Open the dropdown
    this.debouncedMouseover = debounce(() => {
      this.openDrawer.open();
    }, 100);
  }

  /**
   * Close the open dropdown when handling a link that does not control a dropdown
   * @param {Event} event - The event object
   */
  handleNavLinkEvent(event) {
    if (
      !event.target.closest('dropdown-element') &&
      !event.target.closest('[data-dropdown-controller]')
    )
      this.querySelector('[aria-hidden="false"]')?.close();
  }

  /**
   * Handle dropdown events in primary nav, find the dropdown and if it exists - handle the action
   * @param {Event} event - The event object
   */
  handleNavDrawerEvent(event) {
    const controller = event.target.closest('[data-dropdown-controller]');
    const dropdownId = controller?.getAttribute('aria-controls');
    if (!dropdownId) return;

    // Close other open dropdowns
    const openDropdown = this.querySelector('[aria-hidden="false"]');
    if (openDropdown && openDropdown.id !== dropdownId) {
      openDropdown.close();
    }

    this.openDrawer = document.getElementById(dropdownId);
    this.debouncedMouseover();
  }

  /**
   * Close all dropdowns
   */
  closeAllNavDrawers() {
    this.dropdowns = this.dropdowns || this.primaryNav.querySelectorAll('dropdown-element');
    this.dropdowns.forEach((dropdown) => dropdown.close());
  }

  /**
   * Set header height in global scope and CSS var
   */
  getHeaderHeight = () => {
    const headerHeight = getElementDimensions('header-element').height;
    window.HEADER_HEIGHT = headerHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  };

  /**
   * Set header top in global scope and CSS var
   */
  updateHeaderTop = (top = 0, offset = 0) => {
    const headerTop = `${top}px`;
    document.documentElement.style.setProperty('--header-top', headerTop);
    document.documentElement.style.setProperty('--header-spacing-y', `${offset}px`);
  };

  /**
   * Handle scroll event and update header visibility/styling
   */
  handleScroll = () => {
    // Update header background if at the top of the page or scrolled back into view
    const isAtTop = window.scrollY <= window.HEADER_HEIGHT;
    this.classList.toggle('header--on-top', isAtTop);
    const header = this.parentElement;
    header.classList.toggle('z-50', isAtTop);

    // Update current scroll position
    const currentScrollPosition = window.scrollY;

    // Don't animate if we're at the top of the page
    if (currentScrollPosition <= window.HEADER_HEIGHT) {
      this.updateHeaderTop(0, window.HEADER_HEIGHT);
      this.lastScrollPosition = currentScrollPosition;
      this.classList.remove('header--scrolling-up');
      return;
    }

    const isScrollingUp = currentScrollPosition < this.lastScrollPosition;
    this.classList.toggle('header--scrolling-up', isScrollingUp);
    if (this.classList.contains('header--scrolling-up')) {
      header.classList.add('z-50');
    }

    isScrollingUp
      ? // Scrolling up = show header
        this.updateHeaderTop(0, window.HEADER_HEIGHT)
      : // Scrolling down = hide header
        this.updateHeaderTop(window.HEADER_HEIGHT, 0);

    this.lastScrollPosition = currentScrollPosition;
  };
}

customElements.define('header-element', HeaderElement);
